import { useEffect, useState } from "react";
import { Briefcase, CheckCircle2, Activity, FileCheck2, UserCog, Server, Clock, Sparkles, Mic, Sheet, Mail, AlertTriangle, RefreshCcw, FolderOpen, CheckCheck } from "lucide-react";
import { fetchDashboard, fetchIntegrationStatus, formatApiError } from "@/lib/api";
import { PageHeader, RangeToggle, StatCard, useRange, fmtInt } from "@/components/dashboard/Primitives";
import DataSourceBadge from "@/components/dashboard/DataSourceBadge";
import HistorySection from "@/components/dashboard/HistorySection";

const SERIES_META = [
    { key: "jobs_today", label: "Jobs i dag", color: "#3b82f6" },
    { key: "jobs_done_today", label: "Jobs lavet i dag", color: "#10b981" },
    { key: "jobs_open", label: "Åbne jobs", color: "#0ea5e9" },
    { key: "jobs_in_progress", label: "Jobs i gang", color: "#8b5cf6" },
    { key: "invoice_ready", label: "Faktura-klar", color: "#f59e0b" },
    { key: "employees_active", label: "Medarbejdere i gang", color: "#ef4444" },
];

export default function Overview() {
    const [data, setData] = useState(null);
    const [intStatus, setIntStatus] = useState({});
    const [range, setRange] = useRange("week");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        setError(null);
        Promise.all([
            fetchDashboard().then((d) => setData(d?.overview || null)),
            fetchIntegrationStatus().then(setIntStatus).catch(() => {}),
        ])
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // Live updates: poll every 30s + refresh when tab regains focus
        const interval = setInterval(load, 30000);
        const onFocus = () => load();
        const onVisibility = () => { if (document.visibilityState === "visible") load(); };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    if (loading && !data) {
        return (
            <div data-testid="overview-loading" className="p-6 text-slate-400 flex items-center gap-2 text-sm">
                <span className="w-3 h-3 rounded-full border-2 border-slate-300 border-t-blue-500 animate-spin" />
                Indlæser…
            </div>
        );
    }

    if (error && !data) {
        return (
            <div data-testid="overview-error" className="p-6 max-w-2xl mx-auto">
                <div className="rounded-2xl border border-red-200 bg-red-50/60 p-5 flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-red-900">Kunne ikke indlæse Overview</h3>
                        <p className="text-sm text-red-800 mt-1 break-words">{error}</p>
                        <button
                            data-testid="overview-retry-btn"
                            onClick={load}
                            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-red-700 hover:text-red-900"
                        >
                            <RefreshCcw className="w-3.5 h-3.5" /> Prøv igen
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const kpis = data.kpis_by_range?.[range] || {
        jobs_today: data.jobs_today, jobs_done_today: data.jobs_done_today,
        jobs_total: data.jobs_total, jobs_done_total: data.jobs_done_total,
        jobs_open: data.jobs_open, jobs_in_progress: data.jobs_in_progress,
        invoice_ready: data.invoice_ready, employees_active: data.employees_active,
    };
    const series = data.series?.[range] || {};
    const labels = series.labels || [];
    const lineSeries = SERIES_META.map((m) => ({ ...m, data: series[m.key] || [] }));

    return (
        <div data-testid="overview-page" className="p-5 sm:p-8 max-w-7xl mx-auto">
            <PageHeader
                title="Overview"
                subtitle="Et hurtigt overblik over jobs, medarbejdere og drift."
                right={
                    <div className="flex items-center gap-3">
                        <DataSourceBadge entity="summary" />
                        <RangeToggle value={range} onChange={setRange} testid="overview-range" />
                    </div>
                }
            />

            <div className="mb-4 bg-blue-50/60 border border-blue-100 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-blue-900">
                <Sparkles className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
                <p>Data opdateres automatisk fra MyServiceHelpers database, ElevenLabs og aktive integrationer. Tal og grafer skifter efter valgt periode.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard testid="stat-jobs-today" label="Jobs i dag" value={fmtInt(kpis.jobs_today)} hint={`Total: ${fmtInt(kpis.jobs_total)}`} icon={Briefcase} accent="blue" />
                <StatCard testid="stat-jobs-done-today" label="Jobs lavet i dag" value={fmtInt(kpis.jobs_done_today)} hint={`Totalt lavet: ${fmtInt(kpis.jobs_done_total)}`} icon={CheckCheck} accent="emerald" />
                <StatCard testid="stat-jobs-open" label="Åbne jobs" value={fmtInt(kpis.jobs_open)} icon={FolderOpen} accent="blue" />
                <StatCard testid="stat-jobs-in-progress" label="Jobs i gang" value={fmtInt(kpis.jobs_in_progress)} icon={Activity} accent="purple" />
                <StatCard testid="stat-invoice-ready" label="Faktura-klar" value={fmtInt(kpis.invoice_ready)} icon={FileCheck2} accent="orange" />
                <StatCard testid="stat-employees-active" label="Medarbejdere i gang" value={fmtInt(kpis.employees_active)} icon={UserCog} accent="blue" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
                <div className="lg:col-span-2">
                    <HistorySection />
                </div>

                <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Driftsstatus</h2>
                        <span className="inline-flex items-center gap-1.5 text-emerald-600 text-xs font-semibold">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Operationel
                        </span>
                    </div>
                    <ul className="space-y-3 text-sm" data-testid="overview-driftsstatus">
                        <li className="flex items-center justify-between"><span className="text-slate-500 flex items-center gap-2"><Server className="w-4 h-4" />API</span><span className="text-emerald-600 font-medium">OK</span></li>
                        <li className="flex items-center justify-between"><span className="text-slate-500 flex items-center gap-2"><Server className="w-4 h-4" />Database</span><span className="text-emerald-600 font-medium">OK</span></li>
                        <li className="flex items-center justify-between">
                            <span className="text-slate-500 flex items-center gap-2"><Server className="w-4 h-4" />Voice Intake</span>
                            <span className={(intStatus.elevenlabs === "active" || intStatus.elevenlabs === "ok") ? "text-emerald-600 font-medium" : "text-slate-400 font-medium"}>
                                {(intStatus.elevenlabs === "active" || intStatus.elevenlabs === "ok") ? "Aktiv" : "Ikke konfigureret"}
                            </span>
                        </li>
                        <li className="flex items-center justify-between">
                            <span className="text-slate-500 flex items-center gap-2"><Server className="w-4 h-4" />Post-Call Worker</span>
                            <span className={(intStatus.elevenlabs === "active" || intStatus.elevenlabs === "ok") ? "text-emerald-600 font-medium" : "text-slate-400 font-medium"}>
                                {(intStatus.elevenlabs === "active" || intStatus.elevenlabs === "ok") ? "Kører" : "Ikke aktiv"}
                            </span>
                        </li>
                        <li className="flex items-center justify-between">
                            <span className="text-slate-500 flex items-center gap-2"><Server className="w-4 h-4" />Email</span>
                            <span className={(intStatus.email === "active" || intStatus.email === "ok") ? "text-emerald-600 font-medium" : "text-slate-400 font-medium"}>
                                {(intStatus.email === "active" || intStatus.email === "ok") ? "OK" : "Ikke konfigureret"}
                            </span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="mt-6 bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Integration status</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                        { k: "elevenlabs", l: "ElevenLabs", Icon: Mic },
                        { k: "email", l: "Email", Icon: Mail },
                    ].map(({ k, l, Icon }) => {
                        const s = intStatus[k] || "not_configured";
                        const cls = s === "active" || s === "ok" ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                            : s === "error" ? "bg-red-50 border-red-100 text-red-700"
                            : s === "inactive" ? "bg-slate-50 border-slate-200 text-slate-500"
                            : "bg-slate-50 border-slate-200 text-slate-500";
                        const label = s === "active" || s === "ok" ? "Aktiv" : s === "error" ? "Fejl" : s === "inactive" ? "Deaktiveret" : s === "configured" ? "Konfigureret" : "Ikke konfigureret";
                        return (
                            <div key={k} data-testid={`overview-int-${k}`} className={`rounded-2xl border p-4 flex items-center gap-3 ${cls}`}>
                                <Icon className="w-5 h-5 shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold uppercase tracking-wider opacity-80">{l}</p>
                                    <p className="text-sm font-bold">{label}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mt-6 bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Seneste aktivitet</h2>
                <ul className="divide-y divide-slate-100">
                    {(data.recent_activity || []).map((a, i) => (
                        <li key={i} className="py-3 flex items-center gap-3 text-sm">
                            <span className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 inline-flex items-center justify-center shrink-0">
                                <Clock className="w-3.5 h-3.5" />
                            </span>
                            <span className="text-slate-500 w-20 shrink-0">{a.time || "—"}</span>
                            <span className="text-slate-800">{a.text || ""}</span>
                        </li>
                    ))}
                    {(data.recent_activity || []).length === 0 && (
                        <li className="py-6 text-center text-slate-400 text-sm">Ingen aktivitet endnu.</li>
                    )}
                </ul>
            </div>
        </div>
    );
}
